import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Calendar, User, LogOut, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { Button } from './ui/button';
import { LOGO_URL } from '../constants/brand';
import api from '../utils/api';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [langMenu, setLangMenu] = useState(false);
  const [giftEnabled, setGiftEnabled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    api.get('/settings/public').then(({ data }) => setGiftEnabled(!!data.gift_vouchers_enabled)).catch(() => {});
  }, []);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/about', label: t.nav.about },
    { to: '/services', label: t.nav.services },
    { to: '/offers', label: t.nav.offers },
    ...(giftEnabled ? [{ to: '/gift-cards', label: 'Gift Cards' }] : []),
    { to: '/gallery', label: t.nav.gallery },
    { to: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'bg-[#04140e]/95 backdrop-blur-xl shadow-lg border-b border-[#c5a059]/20' : 'bg-[#04140e]/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-1">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2.5" data-testid="nav-logo">
            <img src={LOGO_URL} alt="4AN Hair & Beauty Studio" className="h-12 md:h-14 w-auto object-contain" />
            <div className="hidden md:block leading-tight">
              <div className="text-[#c5a059] font-serif font-bold text-base">4AN HAIR</div>
              <div className="text-white/60 text-[9px] tracking-[0.25em]">&amp; BEAUTY STUDIO</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-7">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`text-[13px] tracking-wide transition-colors ${
                  isActive(link.to) ? 'text-[#c5a059] font-semibold' : 'text-white/85 hover:text-[#c5a059]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={() => setLangMenu(!langMenu)}
                data-testid="lang-switcher"
                className="flex items-center space-x-1 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#c5a059]/40 text-[#c5a059] rounded-full hover:bg-[#c5a059]/10"
              >
                <Globe className="w-3 h-3" />
                <span>{(lang || 'nl').toUpperCase()}</span>
              </button>
              {langMenu && (
                <div className="absolute top-full right-0 mt-2 bg-[#0a2418] border border-[#c5a059]/30 rounded-sm shadow-lg py-2 min-w-[110px]">
                  {['nl', 'en', 'fr'].map((code) => (
                    <button
                      key={code}
                      onClick={() => { setLang(code); setLangMenu(false); }}
                      className={`w-full text-left px-4 py-1.5 text-xs hover:bg-[#c5a059]/10 ${lang === code ? 'text-[#c5a059] font-semibold' : 'text-white/80'}`}
                    >
                      {code.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <>
                <Link to={user.role === 'admin' ? '/admin' : '/account'} data-testid={user.role === 'admin' ? 'nav-admin-btn' : 'nav-account-btn'}>
                  <Button variant="outline" size="sm" className="rounded-full border-[#c5a059]/40 text-[#c5a059] bg-transparent hover:bg-[#c5a059]/10 hover:text-[#c5a059] h-8 text-xs">
                    <User className="w-3.5 h-3.5 mr-1.5" />{user.role === 'admin' ? t.nav.admin : t.nav.account}
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout-btn" className="text-white/70 hover:bg-white/10 hover:text-white h-8 w-8 p-0">
                  <LogOut className="w-3.5 h-3.5" />
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login-btn">
                <Button variant="outline" size="sm" className="rounded-full border-[#c5a059]/40 text-[#c5a059] bg-transparent hover:bg-[#c5a059]/10 hover:text-[#c5a059] h-8 text-xs">
                  {t.nav.login}
                </Button>
              </Link>
            )}
            <Link to="/services" data-testid="nav-book-btn">
              <Button size="sm" className="bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold h-8 text-xs px-4">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />{t.nav.book}
              </Button>
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden mt-3 pb-4 space-y-2" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setIsOpen(false)} className="block py-1.5 text-sm text-white/85">
                {link.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-[#c5a059]/20 space-y-2">
              <div className="flex space-x-2">
                {['nl', 'en', 'fr'].map((code) => (
                  <button key={code} onClick={() => setLang(code)} className={`flex-1 py-2 text-xs border rounded ${lang === code ? 'bg-[#c5a059] text-[#04140e]' : 'border-[#c5a059]/30 text-white/70'}`}>
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
              {user ? (
                <Link to={user.role === 'admin' ? '/admin' : '/account'} onClick={() => setIsOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full">{user.role === 'admin' ? t.nav.admin : t.nav.account}</Button>
                </Link>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full">{t.nav.login}</Button>
                </Link>
              )}
              <Link to="/services" onClick={() => setIsOpen(false)}>
                <Button size="sm" className="w-full bg-[#c5a059] text-[#04140e]">{t.nav.book}</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
